import { ThreadEcho } from "@/components/ThreadEcho";
import { useState } from "react";
import { createReflection, respondReflection } from "@/lib/api";
import { toast } from "sonner";
import { TalkInput } from "@/components/TalkInput";

/**
 * ReflectionCard — Notice → Evidence → Interpret → Check → Recalibrate.
 * Coaching voice uses the light sans .spark-voice style.
 */
export default function ReflectionCard({ userId, activityKey, notes, onRejected, onResolved }) {
  const [loading, setLoading] = useState(false);
  const [reflection, setReflection] = useState(null);
  const [mode, setMode] = useState(null);
  const [correction, setCorrection] = useState("");

  const askForReflection = async () => {
    if (!notes || notes.trim().length < 10) {
      toast("Say a little more so Spark has something to reflect on.");
      return;
    }
    setLoading(true);
    try {
      const r = await createReflection(userId, { activity_key: activityKey, notes });
      setReflection(r);
      setMode(null);
    } finally {
      setLoading(false);
    }
  };

  const respond = async (status) => {
    try {
      if (!reflection) return;
      if (status !== "yes" && !correction.trim()) {
        setMode(status);
        return;
      }
      const updated = await respondReflection(reflection.id, { status, correction });
      setReflection(updated);
      setMode("done");
      if (status === "no") onRejected?.(updated);
      onResolved?.(updated);
      if (status === "yes") toast("Held in your Career Compass.");
      else toast("Recalibrated with your correction.");
    } catch (e) {
      toast(e?.response?.data?.detail || "Something interrupted us. Your work is saved — please try that again.");
    }
  };

  if (!reflection) {
    return (
      <div className="spark-voice-block">
        <p className="spark-eyebrow-copper mb-3">Coaching reflection</p>
        <p className="spark-voice text-2xl leading-snug text-[#17171C] max-w-2xl">
          When you're ready, Spark can offer a quiet observation from what you've written.
        </p>
        <p className="mt-3 text-[#56565D]">Reflections are tentative. You always have the final word.</p>
        <button
          onClick={askForReflection}
          disabled={loading}
          data-testid="reflection-ask-btn"
          className="mt-6 spark-btn-primary disabled:opacity-60"
        >
          {loading ? "Reading between the lines…" : "Offer a reflection"}
        </button>
      </div>
    );
  }

  return (
    <div className="spark-voice-block">
      <p className="spark-eyebrow-copper mb-2">What I'm noticing</p>
      <p data-testid="reflection-notice" className="spark-voice text-2xl leading-snug text-[#17171C]">{reflection.notice}</p>

      <p className="spark-eyebrow mt-8 mb-2">The evidence</p>
      <p data-testid="reflection-evidence" className="text-[#56565D] leading-relaxed max-w-2xl">{reflection.evidence}</p>

      <p className="spark-eyebrow-copper mt-8 mb-2">One possibility</p>
      <p data-testid="reflection-interpretation" className="spark-voice text-xl leading-snug" style={{ color: "#1F3D80" }}>
        {reflection.interpretation}
      </p>
      <ThreadEcho theme={reflection.thread_echo} className="mt-3" />

      {reflection.status === "pending" && mode !== "partly" && mode !== "no" && (
        <>
          <p className="mt-8 text-sm text-[#8A8A91]">Does that feel accurate?</p>
          <div className="mt-3 flex flex-wrap gap-2">
            <button data-testid="reflection-yes-btn" onClick={() => respond("yes")} className="spark-btn-copper">Yes</button>
            <button data-testid="reflection-partly-btn" onClick={() => setMode("partly")} className="spark-btn-secondary">Partly</button>
            <button data-testid="reflection-no-btn" onClick={() => setMode("no")} className="spark-btn-secondary">No</button>
          </div>
        </>
      )}

      {(mode === "partly" || mode === "no") && reflection.status === "pending" && (
        <div className="mt-5 max-w-2xl">
          <TalkInput
            testid="reflection-correction-input"
            value={correction}
            onChange={setCorrection}
            rows={3}
            placeholder={mode === "partly" ? "Which part fits, and which part doesn't?" : "Tell me what I'm missing."}
          />
          <div className="mt-3 flex gap-2">
            <button data-testid="reflection-submit-correction-btn" onClick={() => respond(mode)} className="spark-btn-primary">Save correction</button>
            <button onClick={() => { setMode(null); setCorrection(""); }} className="spark-btn-secondary">Cancel</button>
          </div>
        </div>
      )}

      {reflection.status !== "pending" && (
        <div className="mt-8 flex items-center gap-2 text-sm text-[#8A8A91]">
          <span className="h-1.5 w-1.5 rounded-full bg-[#2C50A0]" />
          {reflection.status === "confirmed" && "Held in your Career Compass."}
          {reflection.status === "partly" && "Recalibrated with your correction."}
          {reflection.status === "no" && "Set aside. Spark won't build on this interpretation."}
        </div>
      )}
    </div>
  );
}
